// apps/provider/src/services/api/messaging.js
// Message threads, messages, participants, attachments, read status,
// search, unread counts, and composite operations.
//
// Methods: getMessageThreads, getMessageThread, createMessageThread, updateMessageThread,
//          archiveMessageThread, closeMessageThread,
//          getThreadParticipants, addThreadParticipant, removeThreadParticipant,
//          getMessages, sendMessage, updateMessage, editMessage, deleteMessage,
//          markMessageRead, markThreadRead, getUnreadThreadCount,
//          assignThread, setThreadPriority, setThreadCategory,
//          searchMessages, searchThreads,
//          getUnreadMessageCount, getUnreadThreadsCount,
//          uploadMessageAttachment, getMessageAttachments, getThreadAttachments, deleteMessageAttachment,
//          sendMessageWithAttachments, getPatientMessageThreads
//
// Cross-domain calls (via composed `this`):
//   sendMessageWithAttachments → this.sendMessage, this.uploadMessageAttachment, this.updateMessage
//
// External imports: authService (for uploadMessageAttachment user ID), isDemoEnvironment (for private storage flag)
//
// Response types: @prism-ehr/shared → MessageThread, Message, MessageAttachment,
//                 MessageWithAttachmentsResult, UnreadCountResult

import { authService } from '../auth';
import { isDemoEnvironment } from '@/config/platforms';

/**
 * @returns {Object} Messaging domain methods (bound to `this` of the composed apiService)
 */
export function createMessagingMethods() {
  return {

    // ============ Thread Management ============

    /**
     * Get message threads with optional filters.
     *
     * Xano: GET /message_thread
     * Xano staging file: 650_message_thread_GET.xs
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.patient_id]
     * @param {string} [filters.sender_id]
     * @param {string[]} [filters.category]
     * @param {string[]} [filters.status]
     * @param {string[]} [filters.priority]
     * @param {boolean} [filters.assigned_to_me]
     * @param {boolean} [filters.unread_only]
     * @param {boolean} [filters.has_attachments]
     * @param {boolean} [filters.requires_action]
     * @param {number} [filters.page]
     * @param {number} [filters.limit]
     * @param {string} [filters.sort_by]
     * @param {string} [filters.sort_direction]
     * @returns {Promise<import('@prism-ehr/shared').MessageThread[]>}
     */
    async getMessageThreads(filters = {}) {
      const queryParams = new URLSearchParams();

      if (filters.patient_id) queryParams.append('patient_id', filters.patient_id);
      if (filters.sender_id) queryParams.append('sender_id', filters.sender_id);
      if (filters.participant_user_id) queryParams.append('participant_user_id', filters.participant_user_id);
      if (filters.category?.length) filters.category.forEach(cat => queryParams.append('category', cat));
      if (typeof (filters?.status) == 'array' && filters.status?.length > 0) filters.status.forEach(stat => queryParams.append('status', stat));
      if (filters.priority?.length) filters.priority.forEach(pri => queryParams.append('priority', pri));
      if (filters.assigned_to_me !== undefined) queryParams.append('assigned_to_me', filters.assigned_to_me);
      if (filters.unread_only !== undefined) queryParams.append('unread_only', filters.unread_only);
      if (filters.has_attachments !== undefined) queryParams.append('has_attachments', filters.has_attachments);
      if (filters.requires_action !== undefined) queryParams.append('requires_action', filters.requires_action);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit || 20);
      if (filters.sort_by) queryParams.append('sort_by', filters.sort_by || 'last_message_at');
      if (filters.sort_direction) queryParams.append('sort_direction', filters.sort_direction || 'desc');

      return this.request(`/message_thread?${queryParams.toString()}`, {
        method: 'GET'
      }, 'getMessageThreads');
    },

    /**
     * Get a single message thread by ID.
     *
     * Xano: GET /message_thread/{message_thread_id}
     * Xano staging file: 649_message_thread_message_thread_id_GET.xs
     *
     * @param {string} threadId - Thread UUID
     * @returns {Promise<import('@prism-ehr/shared').MessageThread>}
     */
    async getMessageThread(threadId) {
      return this.request(`/message_thread/${threadId}`, {
        method: 'GET'
      }, 'getMessageThread');
    },

    /**
     * Create a new message thread.
     *
     * Xano: POST /message_thread
     * Xano staging file: 651_message_thread_POST.xs
     *
     * Xano auto-populates: organization_id (from auth.extras), last_message_at (now), created_at (now).
     *
     * @param {Object} threadData
     * @param {string} threadData.patient_id
     * @param {string} [threadData.patient_name]
     * @param {string} [threadData.sender_id]
     * @param {string} [threadData.sender_name]
     * @param {string} [threadData.subject]
     * @param {string} [threadData.category]
     * @param {string} [threadData.priority]
     * @param {Array} [threadData.participants]
     * @param {string} [threadData.initial_message]
     * @param {string} [threadData.last_message_preview]
     * @param {string} [threadData.last_sender_name]
     * @returns {Promise<import('@prism-ehr/shared').MessageThread>}
     */
    async createMessageThread(threadData) {
      return this.request('/message_thread', {
        method: 'POST',
        body: JSON.stringify({
          patient_id: threadData.patient_id,
          patient_name: threadData.patient_name,
          last_message_at: threadData.last_message_at || Date.now(),
          sender_id: threadData.sender_id,
          sender_name: threadData.sender_name,
          last_message_preview: threadData.last_message_preview,
          last_sender_name: threadData.last_sender_name,
          subject: threadData.subject,
          category: threadData.category || 'general',
          priority: threadData.priority || 'medium',
          participants: threadData.participants || [],
          initial_message: threadData.initial_message,
          created_at: Date.now(),
          status: 'open'
        })
      }, 'createMessageThread');
    },

    /**
     * Update an existing message thread.
     *
     * Xano: PATCH /message_thread/{message_thread_id}
     * Xano staging file: 652_message_thread_message_thread_id_PATCH.xs
     *
     * @param {string} threadId - Thread UUID
     * @param {Object} updates - Fields to update
     * @returns {Promise<import('@prism-ehr/shared').MessageThread>}
     */
    async updateMessageThread(threadId, updates) {
      return this.request(`/message_thread/${threadId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          ...updates,
          updated_at: Date.now()
        })
      }, 'updateMessageThread');
    },

    /**
     * Archive a message thread (sets status to 'archived').
     * Wrapper around updateMessageThread.
     *
     * @param {string} threadId - Thread UUID
     * @returns {Promise<import('@prism-ehr/shared').MessageThread>}
     */
    async archiveMessageThread(threadId) {
      return this.updateMessageThread(threadId, {
        status: 'archived',
        archived_at: Date.now()
      });
    },

    /**
     * Close a message thread (sets status to 'closed').
     * Wrapper around updateMessageThread.
     *
     * @param {string} threadId - Thread UUID
     * @returns {Promise<import('@prism-ehr/shared').MessageThread>}
     */
    async closeMessageThread(threadId) {
      return this.updateMessageThread(threadId, {
        status: 'closed',
        closed_at: Date.now()
      });
    },

    // ============ Thread Participants ============

    /**
     * Get participants for a message thread.
     *
     * Xano: GET /message_thread/{message_thread_id}/participants
     * Xano staging file: 901/902/903_message_thread_message_thread_id_participants_*.xs
     *
     * @param {string} threadId - Thread UUID
     * @returns {Promise<import('@prism-ehr/shared').MessageThreadParticipant[]>}
     */
    async getThreadParticipants(threadId) {
      return this.request(`/message_thread/${threadId}/participants`, {
        method: 'GET'
      }, 'getThreadParticipants');
    },

    /**
     * Add a participant to a message thread.
     *
     * Xano: POST /message_thread/{message_thread_id}/participants
     * Xano staging file: 901/902/903_message_thread_message_thread_id_participants_*.xs
     *
     * @param {string} threadId - Thread UUID
     * @param {string} userId - User ID to add
     * @param {string} userName - Display name for the participant
     * @param {string} [role='staff'] - Participant role
     * @returns {Promise<import('@prism-ehr/shared').MessageThreadParticipant>}
     */
    async addThreadParticipant(threadId, userId, userName, role = 'staff') {
      return this.request(`/message_thread/${threadId}/participants`, {
        method: 'POST',
        body: JSON.stringify({
          user_id: userId,
          user_name: userName,
          role: role,
          notification_preference: 'all'
        })
      }, 'addThreadParticipant');
    },

    /**
     * Remove a participant from a message thread.
     *
     * Xano: DELETE /message_thread/{message_thread_id}/participants/{userId}
     * Xano staging file: 901/902/903_message_thread_message_thread_id_participants_*.xs
     *
     * @param {string} threadId - Thread UUID
     * @param {string} userId - User ID to remove
     * @returns {Promise<void>}
     */
    async removeThreadParticipant(threadId, userId) {
      return this.request(`/message_thread/${threadId}/participants/${userId}`, {
        method: 'DELETE'
      }, 'removeThreadParticipant');
    },

    // ============ Messages ============

    /**
     * Get messages for a thread with optional pagination.
     *
     * Xano: GET /message_thread/{message_thread_id}/messages
     * Xano staging file: 774_message_thread_message_thread_id_messages_GET.xs
     *
     * @param {string} threadId - Thread UUID
     * @param {Object} [pagination={}]
     * @param {number} [pagination.page]
     * @param {number} [pagination.limit]
     * @param {string} [pagination.before] - Cursor: messages before this timestamp
     * @param {string} [pagination.after] - Cursor: messages after this timestamp
     * @returns {Promise<import('@prism-ehr/shared').Message[]>}
     */
    async getMessages(threadId, pagination = {}) {
      const queryParams = new URLSearchParams();
      if (pagination.page) queryParams.append('thread_id', threadId);
      if (pagination.page) queryParams.append('page', pagination.page);
      if (pagination.limit) queryParams.append('limit', pagination.limit || 50);
      if (pagination.before) queryParams.append('before', pagination.before);
      if (pagination.after) queryParams.append('after', pagination.after);

      return this.request(`/message_thread/${threadId}/messages?${queryParams.toString()}`, {
        method: 'GET'
      }, 'getMessages');
    },

    /**
     * Send a new message in a thread.
     * Sends notifications to non-sender participants.
     *
     * Xano: POST /message
     * Xano staging file: 656_message_POST.xs
     *
     * Side effects: Sends "new_message" notification to all thread participants except sender.
     *
     * @param {string} threadId - Thread UUID
     * @param {Object} messageData
     * @param {string} messageData.sender_id
     * @param {string} [messageData.sender_name]
     * @param {string} messageData.body
     * @param {string} [messageData.body_format='plain']
     * @param {boolean} [messageData.is_urgent=false]
     * @param {boolean} [messageData.requires_response=false]
     * @param {number} [messageData.response_deadline]
     * @param {string} [messageData.parent_message_id]
     * @param {string[]} [messageData.attachments]
     * @param {string[]} [messageData.mentioned_user_ids]
     * @returns {Promise<import('@prism-ehr/shared').Message>}
     */
    async sendMessage(threadId, messageData) {
      return this.request(`/message`, {
        method: 'POST',
        body: JSON.stringify({
          thread_id: threadId,
          sender_id: messageData.sender_id,
          sender_name: messageData.sender_name,
          body: messageData.body,
          body_format: messageData.body_format || 'plain',
          is_urgent: messageData.is_urgent || false,
          requires_response: messageData.requires_response || false,
          response_deadline: messageData.response_deadline,
          parent_message_id: messageData.parent_message_id,
          attachments: messageData.attachments || [],
          mentioned_user_ids: messageData.mentioned_user_ids || [],
          created_at: Date.now(),
          delivery_status: 'pending'
        })
      }, 'sendMessage');
    },

    /**
     * Update a message.
     *
     * Xano: PATCH /message/{message_id}
     * Xano staging file: 657_message_message_id_PATCH.xs
     *
     * @param {string} messageId - Message UUID
     * @param {Object} updates - Fields to update
     * @returns {Promise<import('@prism-ehr/shared').Message>}
     */
    async updateMessage(messageId, updates) {
      return this.request(`/message/${messageId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          ...updates,
          updated_at: Date.now()
        })
      }, 'updateMessage');
    },

    /**
     * Edit a message's body content (marks as edited).
     * Specialization of updateMessage.
     *
     * Xano: PATCH /message/{message_id}
     * Xano staging file: 657_message_message_id_PATCH.xs
     *
     * @param {string} messageId - Message UUID
     * @param {string} content - New message body
     * @param {string} originalBody - The original body to preserve before the edit
     * @returns {Promise<import('@prism-ehr/shared').Message>}
     */
    async editMessage(messageId, content, originalBody) {
      return this.request(`/message/${messageId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          body: content,
          edited: true,
          edited_at: Date.now(),
          original_body: originalBody
        })
      }, 'editMessage');
    },

    /**
     * Delete a message.
     *
     * Xano: DELETE /message/{message_id}
     * Xano staging file: 653_message_message_id_DELETE.xs
     *
     * @param {string} messageId - Message UUID
     * @returns {Promise<void>}
     */
    async deleteMessage(messageId) {
      return this.request(`/message/${messageId}`, {
        method: 'DELETE'
      }, 'deleteMessage');
    },

    // ============ Read Status ============

    /**
     * Mark a single message as read.
     *
     * Xano: POST /message/{message_id}/read
     * Xano staging file: Not found in staging
     *
     * @param {string} messageId - Message UUID
     * @returns {Promise<void>}
     */
    async markMessageRead(messageId) {
      return this.request(`/message/${messageId}/read`, {
        method: 'POST',
        body: JSON.stringify({
          read_at: Date.now()
        })
      }, 'markMessageRead');
    },

    /**
     * Mark all messages in a thread as read.
     *
     * Xano: POST /message_thread/{message_thread_id}/read
     * Xano staging file: 904_message_thread_message_thread_id_read_POST.xs
     *
     * @param {string} threadId - Thread UUID
     * @returns {Promise<void>}
     */
    async markThreadRead(threadId) {
      return this.request(`/message_thread/${threadId}/read`, {
        method: 'POST',
        body: JSON.stringify({
          read_at: Date.now()
        })
      }, 'markThreadRead');
    },

    /**
     * Get unread thread count for a user.
     *
     * Xano: GET /message_thread/unread-count
     * Xano staging file: Not found in staging
     *
     * @param {string} userId - User ID
     * @returns {Promise<import('@prism-ehr/shared').UnreadCountResult>}
     */
    async getUnreadThreadCount(userId) {
      return this.request(`/message_thread/unread-count?user_id=${userId}`, {
        method: 'GET'
      }, 'getUnreadThreadCount');
    },

    // ============ Thread Actions (wrappers around updateMessageThread) ============

    /**
     * Assign a thread to a user.
     * Wrapper around updateMessageThread.
     *
     * @param {string} threadId - Thread UUID
     * @param {string} userId - User ID to assign
     * @returns {Promise<import('@prism-ehr/shared').MessageThread>}
     */
    async assignThread(threadId, userId) {
      return this.updateMessageThread(threadId, {
        assigned_to: userId,
        assigned_at: Date.now()
      });
    },

    /**
     * Set the priority of a thread.
     * Wrapper around updateMessageThread.
     *
     * @param {string} threadId - Thread UUID
     * @param {string} priority - Priority level (e.g. 'low', 'medium', 'high', 'urgent')
     * @returns {Promise<import('@prism-ehr/shared').MessageThread>}
     */
    async setThreadPriority(threadId, priority) {
      return this.updateMessageThread(threadId, { priority });
    },

    /**
     * Set the category of a thread.
     * Wrapper around updateMessageThread.
     *
     * @param {string} threadId - Thread UUID
     * @param {string} category - Category string
     * @returns {Promise<import('@prism-ehr/shared').MessageThread>}
     */
    async setThreadCategory(threadId, category) {
      return this.updateMessageThread(threadId, { category });
    },

    // ============ Search ============

    /**
     * Full-text search across messages.
     *
     * Xano: GET /messages/search
     * Xano staging file: 775_messages_search_GET.xs
     *
     * Uses Xano full-text search with rank scoring. Max 200 results.
     *
     * @param {string} query - Search text
     * @param {Object} [filters={}]
     * @param {string} [filters.patient_id]
     * @param {string} [filters.thread_id]
     * @param {string} [filters.category]
     * @param {string} [filters.date_from]
     * @param {string} [filters.date_to]
     * @param {number} [filters.limit]
     * @returns {Promise<import('@prism-ehr/shared').Message[]>}
     */
    async searchMessages(query, filters = {}) {
      const queryParams = new URLSearchParams();

      queryParams.append('q', query);
      if (filters.patient_id) queryParams.append('patient_id', filters.patient_id);
      if (filters.thread_id) queryParams.append('thread_id', filters.thread_id);
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.date_from) queryParams.append('date_from', filters.date_from);
      if (filters.date_to) queryParams.append('date_to', filters.date_to);
      if (filters.limit) queryParams.append('limit', filters.limit || 20);

      return this.request(`/messages/search?${queryParams.toString()}`, {
        method: 'GET'
      }, 'searchMessages');
    },

    /**
     * Search message threads.
     *
     * Xano: GET /message_thread/search
     * Xano staging file: Not found in staging
     *
     * @param {string} query - Search text
     * @param {Object} [filters={}]
     * @param {string} [filters.patient_id]
     * @param {string} [filters.category]
     * @param {string} [filters.status]
     * @param {number} [filters.limit]
     * @returns {Promise<import('@prism-ehr/shared').MessageThread[]>}
     */
    async searchThreads(query, filters = {}) {
      const queryParams = new URLSearchParams();

      queryParams.append('q', query);
      if (filters.patient_id) queryParams.append('patient_id', filters.patient_id);
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.limit) queryParams.append('limit', filters.limit || 20);

      return this.request(`/message_thread/search?${queryParams.toString()}`, {
        method: 'GET'
      }, 'searchThreads');
    },

    // ============ Unread Counts ============

    /**
     * Get unread message count for a user.
     *
     * Xano: GET /message/unread-count
     * Xano staging file: Not found in staging
     *
     * @param {string} userId - User ID
     * @returns {Promise<import('@prism-ehr/shared').UnreadCountResult>}
     */
    async getUnreadMessageCount(userId) {
      return this.request(`/message/unread-count?user_id=${userId}`, {
        method: 'GET'
      }, 'getUnreadMessageCount');
    },

    /**
     * Get unread threads count for a user.
     * Iterates all threads and compares participant last_read_at vs last_message_at.
     *
     * Xano: GET /message_threads/unread-count
     * Xano staging file: 767_message_threads_unread_count_GET.xs
     *
     * Note: Uses $auth.id server-side (user_id param is ignored).
     *
     * @param {string} userId - User ID (ignored server-side, uses auth)
     * @returns {Promise<import('@prism-ehr/shared').UnreadCountResult>}
     */
    async getUnreadThreadsCount(userId) {
      return this.request(`/message_threads/unread-count?user_id=${userId}`, {
        method: 'GET'
      }, 'getUnreadThreadsCount');
    },

    // ============ Attachments ============

    /**
     * Upload a file attachment to a message.
     * Converts file to base64, uploads via Xano storage.create_attachment.
     *
     * Xano: POST /message_attachment
     * Xano staging file: 771_message_attachment_POST.xs
     *
     * @param {File} file - Browser File object
     * @param {string} messageId - Message UUID
     * @param {string} threadId - Thread UUID
     * @returns {Promise<import('@prism-ehr/shared').MessageAttachment>}
     */
    async uploadMessageAttachment(file, messageId, threadId) {
      const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = error => reject(error);
        });
      };

      const base64Data = await fileToBase64(file);

      const response = await this.request('/message_attachment', {
        method: 'POST',
        body: JSON.stringify({
          message_id: messageId,
          thread_id: threadId,
          file_name: file.name,
          file_size: file.size,
          mime_type: file.type,
          file_data_string: base64Data,
          uploaded_by: authService.getUserProfile().id,
          created_at: Date.now(),
          scan_status: 'pending',
          private_storage: !isDemoEnvironment()
        })
      }, 'uploadMessageAttachment');

      return response;
    },

    /**
     * Get attachments for a specific message.
     *
     * Xano: GET /message_attachment
     * Xano staging file: 770_message_attachment_GET.xs
     *
     * @param {string} messageId - Message UUID
     * @returns {Promise<import('@prism-ehr/shared').MessageAttachment[]>}
     */
    async getMessageAttachments(messageId) {
      return this.request(`/message_attachment?message_id=${messageId}`, {
        method: 'GET'
      }, 'getMessageAttachments');
    },

    /**
     * Get all attachments for a thread.
     *
     * Xano: GET /message_attachment
     * Xano staging file: 770_message_attachment_GET.xs
     *
     * @param {string} threadId - Thread UUID
     * @returns {Promise<import('@prism-ehr/shared').MessageAttachment[]>}
     */
    async getThreadAttachments(threadId) {
      return this.request(`/message_attachment?thread_id=${threadId}`, {
        method: 'GET'
      }, 'getThreadAttachments');
    },

    /**
     * Delete a message attachment.
     *
     * Xano: DELETE /message_attachment/{message_attachment_id}
     * Xano staging file: 768_message_attachment_message_attachment_id_DELETE.xs
     *
     * @param {string} attachmentId - Attachment UUID
     * @returns {Promise<void>}
     */
    async deleteMessageAttachment(attachmentId) {
      return this.request(`/message_attachment/${attachmentId}`, {
        method: 'DELETE'
      }, 'deleteMessageAttachment');
    },

    // ============ Composite Methods ============

    /**
     * Send a message with file attachments.
     * Creates the message first, then uploads each attachment, then updates
     * the message record with attachment IDs.
     *
     * Cross-domain: calls this.sendMessage, this.uploadMessageAttachment, this.updateMessage.
     *
     * @param {string} threadId - Thread UUID
     * @param {Object} messageData - Same as sendMessage messageData
     * @param {File[]} [files=[]] - Browser File objects to attach
     * @returns {Promise<import('@prism-ehr/shared').Message>}
     */
    async sendMessageWithAttachments(threadId, messageData, files = []) {
      try {
        // Step 1: Create the message
        const messageResponse = await this.sendMessage(threadId, {
          thread_id: threadId,
          sender_id: messageData.sender_id,
          sender_name: messageData.sender_name,
          body: messageData.body,
          body_format: messageData.body_format || 'plain',
          is_urgent: messageData.is_urgent || false,
          requires_response: messageData.requires_response || false,
          response_deadline: messageData.response_deadline,
          created_at: Date.now(),
          delivery_status: 'pending',
          contains_attachments: files.length > 0
        });

        // Step 2: Upload attachments and link to message
        if (files.length > 0) {
          const attachmentPromises = files.map(file =>
            this.uploadMessageAttachment(file, messageResponse.id, threadId)
          );

          const attachmentResults = await Promise.all(attachmentPromises);

          // Step 3: Update message with attachment references
          await this.updateMessage(messageResponse.id, {
            attachments: attachmentResults.map(a => ({
              id: a.id,
              file_name: a.file_name,
              file_url: a.file_url,
              mime_type: a.mime_type,
              file_size: a.file_size
            }))
          });
        }

        return messageResponse;
      } catch (error) {
        console.error('Error sending message with attachments:', error);
        throw error;
      }
    },

    // ============ Convenience Methods ============

    /**
     * Get a patient's existing open message threads (for thread selection).
     * Convenience wrapper around getMessageThreads with patient-specific filters.
     *
     * Xano: GET /message_thread (same endpoint, filtered)
     * Xano staging file: 650_message_thread_GET.xs
     *
     * @param {string} patientId - Patient UUID
     * @param {number} [limit=5] - Max threads to return
     * @returns {Promise<import('@prism-ehr/shared').MessageThread[]>}
     */
    async getPatientMessageThreads(patientId, limit = 5) {
      return this.request(
        `/message_thread?patient_id=${patientId}&status=open&limit=${limit}&sort_by=last_message_at&sort_direction=desc`,
        { method: 'GET' }
      );
    },

  };
}
